import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo, useState} from 'react';

import CollapsibleTransitionCard from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/CollapsibleTransitionCard';
import {FONT_FAMILY_MONOSPACED} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {BLUE} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import copyToClipboard from 'util/clipboard/copyToClipboard';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';

interface SchemaFieldInfo {
  name: string;
  systemName: string;
  isArchived: boolean;
  archivePurpose: string | null;
}

interface SchemaSection {
  id: string;
  name: string;
  inputFields: Array<SchemaFieldInfo>;
  outputFields: Array<SchemaFieldInfo>;
}

function parseFields(fieldsJson: unknown): Array<SchemaFieldInfo> {
  if (!Array.isArray(fieldsJson)) {
    return [];
  }
  return fieldsJson
    .filter(
      (f): f is {name: string; systemName?: string; archiveRecord?: {purpose?: string} | null} =>
        f != null && typeof f.name === 'string'
    )
    .map((f) => ({
      name: f.name,
      systemName: f.systemName ?? '',
      isArchived: f.archiveRecord != null,
      archivePurpose:
        f.archiveRecord != null && typeof f.archiveRecord.purpose === 'string'
          ? f.archiveRecord.purpose
          : null,
    }));
}

function buildSchemaSections(
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL,
  availableTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>
): Array<SchemaSection> {
  const seenIds = new Set<string>();
  const sections: Array<SchemaSection> = [];

  const addSchemaSection = (schema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL): void => {
    if (seenIds.has(schema.id)) {
      return;
    }
    seenIds.add(schema.id);
    sections.push({
      id: schema.id,
      name: schema.name,
      inputFields: parseFields(schema.allFields),
      outputFields: schema.outputSchema != null ? parseFields(schema.outputSchema.allFields) : [],
    });
  };

  const sortedSchemas = availableTaskSchemas
    .filter((s) => s.id !== rootTaskSchema.id)
    .toSorted((a, b) => a.name.localeCompare(b.name));

  addSchemaSection(rootTaskSchema);
  for (const schema of sortedSchemas) {
    addSchemaSection(schema);
  }

  return sections;
}

function FieldsTable(props: {
  fields: Array<SchemaFieldInfo>;
  headerLabel: string;
  dataTestComponent: string;
}): JSX.Element | null {
  const {fields, headerLabel, dataTestComponent} = props;
  const [showArchived, setShowArchived] = useState(false);

  const activeFields = useMemo(() => fields.filter((f) => !f.isArchived), [fields]);
  const archivedFields = useMemo(() => fields.filter((f) => f.isArchived), [fields]);

  if (fields.length === 0) {
    return null;
  }

  const visibleFields = showArchived ? [...activeFields, ...archivedFields] : activeFields;
  const archivedStartIndex = activeFields.length;
  const hasArchivedToggleRow = !showArchived && archivedFields.length > 0;
  const totalRows = visibleFields.length + (hasArchivedToggleRow ? 1 : 0);

  return (
    <div className={css(styles.tableWrapper)}>
      <Table
        numRows={totalRows}
        calculateInView={false}
        fitContent={false}
        frozenHeader={true}
        getRowKey={(rowIndex) =>
          rowIndex === visibleFields.length ? '__show_archived__' : visibleFields[rowIndex].name
        }
        includeNegativeMargins={false}
        isCompact={true}
        overflowVisible={true}
        tableLayout="fixed"
      >
        <Column
          header={<Cell className={css(styles.headerCell)}>{headerLabel}</Cell>}
          renderCell={(rowIdx: number) => {
            if (rowIdx === visibleFields.length) {
              return (
                <Cell>
                  <a
                    href=""
                    className={css(styles.showArchivedLink)}
                    onClick={(e) => {
                      e.preventDefault();
                      setShowArchived(true);
                    }}
                    {...getTestProps({component: dataTestComponent, element: 'showArchived'})}
                  >
                    Show archived fields ({archivedFields.length})
                  </a>
                </Cell>
              );
            }
            const field = visibleFields[rowIdx];
            return (
              <Cell
                className={css(
                  styles.fieldNameCell,
                  showArchived && rowIdx >= archivedStartIndex && styles.archivedField
                )}
              >
                {field.name}
                {field.archivePurpose != null && ` (${field.archivePurpose})`}
              </Cell>
            );
          }}
        />
        <Column
          header={<Cell className={css(styles.headerCell)}>System Name</Cell>}
          renderCell={(rowIdx: number) => {
            if (rowIdx === visibleFields.length) {
              return <Cell />;
            }
            const systemName = visibleFields[rowIdx].systemName;
            return (
              <Cell
                className={css(
                  styles.systemNameCell,
                  showArchived && rowIdx >= archivedStartIndex && styles.archivedField
                )}
              >
                <div className="clipboardIcon-container">
                  {systemName}
                  <span
                    className={`clipboardIcon ${css(styles.copyIcon)}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(systemName, {showToastOnSuccess: true});
                    }}
                    {...getTestProps({component: dataTestComponent, element: 'copySystemName'})}
                  >
                    <BenchlingIcon icon="copy" />
                  </span>
                </div>
              </Cell>
            );
          }}
        />
      </Table>
    </div>
  );
}

export interface FlowchartSystemNamesTabProps {
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  availableTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>;
}

export default function FlowchartSystemNamesTab(props: FlowchartSystemNamesTabProps): JSX.Element {
  const {rootTaskSchema, availableTaskSchemas} = props;

  const sections = useMemo(
    () => buildSchemaSections(rootTaskSchema, availableTaskSchemas),
    [rootTaskSchema, availableTaskSchemas]
  );

  return (
    <div className={css(styles.container)}>
      {sections.map((section) => (
        <CollapsibleTransitionCard
          key={section.id}
          header={section.name}
          isOpenByDefault={true}
          fullWidthChildren={true}
          dataTestAttrs={{
            component: 'FlowchartSystemNamesTab',
            element: 'schemaAccordion',
            key: section.id,
          }}
        >
          <div className={css(styles.tablesContainer)}>
            <FieldsTable
              fields={section.inputFields}
              headerLabel="Input Fields"
              dataTestComponent="FlowchartSystemNamesTab-inputFields"
            />
            <FieldsTable
              fields={section.outputFields}
              headerLabel="Output Fields"
              dataTestComponent="FlowchartSystemNamesTab-outputFields"
            />
          </div>
        </CollapsibleTransitionCard>
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ':global': {
      '.mantine-Accordion-control': {
        marginBottom: 0,
      },
    },
  },
  tablesContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '0 12px',
  },
  tableWrapper: {
    ':global': {
      '.simpleTable': {
        overflowX: 'visible',
        marginBottom: 0,
      },
    },
  },
  headerCell: {
    fontWeight: 600,
    fontSize: '13px',
  },
  fieldNameCell: {
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    fontSize: '14px',
    lineHeight: '16px',
    fontWeight: 400,
    color: Color.GRAY_900,
  },
  systemNameCell: {
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    color: Color.GRAY_600,
    fontFamily: FONT_FAMILY_MONOSPACED,
    fontWeight: 500,
    fontSize: '13px',
    lineHeight: 1,
  },
  archivedField: {
    opacity: 0.6,
  },
  copyIcon: {
    cursor: 'pointer',
    marginLeft: '8px',
    color: Color.GRAY_500,
    ':hover': {
      color: Color.GRAY_700,
    },
  },
  showArchivedLink: {
    color: BLUE,
    fontSize: '13px',
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline',
    },
  },
});
